import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ticketValidationService from '../services/ticketValidationService';
import { Ticket, User, Mail, Phone, Building, ArrowLeft, CheckCircle } from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const { registerWithTicket } = useAuth();
  const [formData, setFormData] = useState({
    ticketNumber: '',
    name: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    bio: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [ticketValidated, setTicketValidated] = useState(false);
  const [ticketData, setTicketData] = useState(null);

  useEffect(() => {
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateTicket = async () => {
    if (!formData.ticketNumber.trim()) {
      setErrors({ ticketNumber: 'Ticket number is required' });
      return;
    }

    if (!formData.email.trim()) {
      setErrors({ email: 'Email is required to validate ticket' });
      return;
    }

    // Validate email format
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    const validation = ticketValidationService.validateTicket(formData.ticketNumber, formData.email);
    
    if (validation.valid) {
      setTicketData(validation.ticketData);
      setTicketValidated(true);
      setErrors({});
      
      // Pre-fill form with ticket data (but keep the email they entered if it matches)
      setFormData(prev => ({
        ...prev,
        name: validation.ticketData.name,
        email: formData.email, // Keep the email they entered (already validated to match)
        company: validation.ticketData.company
      }));
      
    } else {
      setErrors({ 
        ticketNumber: validation.error.includes('Email') ? '' : validation.error,
        email: validation.error.includes('Email') ? validation.error : ''
      });
      setTicketValidated(false);
      setTicketData(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!ticketValidated) {
      newErrors.ticketNumber = 'Please validate your ticket first';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const result = await registerWithTicket(formData, ticketData);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'Registration failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="mb-8">
        <Link to="/" className="flex items-center text-gray-600 hover:text-primary-600 mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Register with Ticket</h1>
        <p className="text-gray-600">Enter your ticket number to register for the conference</p>
      </div>

      <div className="card">
        {errors.general && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Ticket Number Section */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-center space-x-2 mb-2">
              <Ticket className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">Ticket Validation</h3>
            </div>
            
            <div className="space-y-3">
              <div>
                <label htmlFor="ticketNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Ticket Number
                </label>
                <div className="relative">
                  <Ticket className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    id="ticketNumber"
                    name="ticketNumber"
                    value={formData.ticketNumber}
                    onChange={handleChange}
                    className={`input-field pl-10 ${errors.ticketNumber ? 'border-red-500' : ''}`}
                    placeholder="Enter your ticket number. e.g. 54239500994"
                    disabled={ticketValidated}
                  />
                </div>
                {errors.ticketNumber && <p className="text-red-500 text-sm mt-1">{errors.ticketNumber}</p>}
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`input-field pl-10 ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="Enter your email address"
                    disabled={ticketValidated}
                  />
                </div>
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
              
              {!ticketValidated && (
                <button
                  type="button"
                  onClick={validateTicket}
                  className="btn-secondary w-full"
                >
                  Validate Ticket & Email
                </button>
              )}
              
              {ticketValidated && (
                <div className="flex items-center justify-center p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-green-600 text-sm font-medium">Ticket and Email Validated</span>
                </div>
              )}
            </div>
            
            {ticketValidated && ticketData && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Ticket validated for:</strong> {ticketData.name} ({ticketData.email})
                </p>
                <p className="text-sm text-green-700">
                  <strong>Role:</strong> {ticketData.role} | <strong>School / Affiliation:</strong> {ticketData.company}
                </p>
              </div>
            )}
          </div>

          {/* Personal Information Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Personal Information</h3>
          </div>
          
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input-field pl-10 ${errors.name ? 'border-red-500' : ''}`}
                placeholder="Enter your full name"
              />
            </div>
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`input-field pl-10 ${errors.phone ? 'border-red-500' : ''}`}
                placeholder="Enter your phone number"
              />
            </div>
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
              School / Affiliation
            </label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="input-field pl-10"
                placeholder="Enter your school name"
              />
            </div>
          </div>

          <div>
            <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
              Job Title
            </label>
            <input
              type="text"
              id="jobTitle"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter your job title"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`input-field ${errors.password ? 'border-red-500' : ''}`}
              placeholder="Create a password (min 6 characters)"
            />
            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`input-field ${errors.confirmPassword ? 'border-red-500' : ''}`}
              placeholder="Confirm your password"
            />
            {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
          </div>

          <button
            type="submit"
            disabled={loading || !ticketValidated}
            className="w-full btn-primary py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Registering...' : 'Complete Registration'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Already registered?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register; 